import { Box, Skeleton } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import { KitButton, KitPill } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import { KitBanner, KitEmpty, KitField, KitFormBar, KitInput, KitSelect } from '../common/MobileKitExtras';
import { useAppBarAction } from '../layout/AppBarActions';
import { orderStatusView } from './orderMobileUtils';

/**
 * Faturamento de OS no shell mobile — `prototipos-mobile/ordens/faturamento.html`.
 * Só apresentação: busca das ordens, filtros e o pagamento (PaymentModal / PaymentScreen)
 * vêm de `pages/service_order/Billing.js`.
 */

const FunnelIcon = protoIcon('funnel');
const ReceiptIcon = protoIcon('receipt');

export const parseMoney = (value) => {
    const n = parseFloat(String(value ?? '0').replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
};
export const formatMoney = (cents) => (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const toInput = (value) => (value && value.isValid() ? value.format('YYYY-MM-DD') : '');
const fromInput = (text) => {
    if (!text) return null;
    const parsed = dayjs(text, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed : null;
};

// `.ecard` em linha: checkbox 22px + título/subtítulo + valor
function BillingRow({ order, checked, canSelect, onToggle }) {
    const view = orderStatusView(order.status);
    const created = order.created_at ? dayjs(order.created_at).format('DD/MM') : '';
    return (
        <Box
            component="label"
            sx={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                mt: '10px',
                position: 'relative',
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '16px',
                p: '14px',
                boxShadow: '0 1px 2px rgba(15,23,42,.06)',
                boxSizing: 'border-box',
                fontSize: 14,
                lineHeight: 1.4,
                cursor: canSelect ? 'pointer' : 'default',
            }}
        >
            {canSelect ? (
                <Box
                    component="input"
                    type="checkbox"
                    checked={checked}
                    onChange={onToggle}
                    aria-label={`Selecionar OS ${order.number}`}
                    // Margem padrão do checkbox nativo (o protótipo não a zera).
                    sx={{ width: 22, height: 22, m: '3px 3px 3px 4px', flexShrink: 0, accentColor: (theme) => theme.palette.primary.main }}
                />
            ) : null}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontWeight: 700, lineHeight: 1.4 }}>OS #{order.number} · {order.customer_name || '—'}</Box>
                <Box sx={{ fontSize: 12.5, color: 'text.disabled', lineHeight: 1.4 }}>
                    {view.label.toLowerCase()}{created ? ` ${created}` : ''}
                </Box>
            </Box>
            {canSelect ? (
                <Box sx={{ fontWeight: 700, lineHeight: 1.4 }}>R$ {order.amount ?? '0,00'}</Box>
            ) : (
                <Box sx={{ textAlign: 'right' }}>
                    <Box sx={{ fontWeight: 700, lineHeight: 1.4, mb: '4px' }}>R$ {order.amount ?? '0,00'}</Box>
                    <KitPill tone={view.tone}>{view.label}</KitPill>
                </Box>
            )}
        </Box>
    );
}

export default function MobileBilling({
    orders,
    loading,
    customers,
    filters,
    onApplyFilters,
    onClearFilters,
    selectedIds,
    onToggle,
    canBill,
    onBill,
    billing,
}) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [draft, setDraft] = useState(filters);

    useAppBarAction({
        label: 'Filtrar',
        icon: FunnelIcon,
        onClick: () => {
            setDraft(filters);
            setFiltersOpen(true);
        },
    });

    const selected = orders.filter((order) => selectedIds.includes(order.order_service_id));
    const totalCents = selected.reduce((sum, order) => sum + parseMoney(order.amount), 0);
    const count = selected.length;
    const label = (n) => `${n} ${n === 1 ? 'ordem' : 'ordens'}`;
    const setField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitBanner tone="info" icon={ReceiptIcon} iconSize={18} title={count === 0 ? 'Nenhuma ordem selecionada' : `${label(count)} ${count === 1 ? 'selecionada' : 'selecionadas'}`}>
                Total a faturar: R$ {formatMoney(totalCents)}
            </KitBanner>

            <Box sx={{ mt: '12px' }} />

            {loading && orders.length === 0 && (
                <Box sx={{ mt: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={74} sx={{ borderRadius: '16px' }} />)}
                </Box>
            )}

            {!loading && orders.length === 0 && (
                <KitEmpty icon={ReceiptIcon} title="Nenhuma ordem para faturar">
                    Ajuste o período ou os filtros para ver outras ordens finalizadas.
                </KitEmpty>
            )}

            {orders.map((order) => (
                <BillingRow
                    key={order.order_service_id}
                    order={order}
                    canSelect={canBill && order.status === 'Finalizada'}
                    checked={selectedIds.includes(order.order_service_id)}
                    onToggle={() => onToggle(order.order_service_id)}
                />
            ))}

            {canBill && (
                <KitFormBar>
                    <Box sx={{ minWidth: 0 }}>
                        <Box component="small" sx={{ display: 'block', fontSize: 11, color: 'text.disabled', fontWeight: 600, lineHeight: 1.4 }}>{label(count)}</Box>
                        <Box component="b" sx={{ fontSize: 18, fontWeight: 800, letterSpacing: '-.02em', lineHeight: 1.4 }}>R$ {formatMoney(totalCents)}</Box>
                    </Box>
                    <KitButton variant="primary" disabled={count === 0 || billing} onClick={onBill}>
                        Faturar
                    </KitButton>
                </KitFormBar>
            )}

            <MobileSheet
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                title="Filtrar"
                footer={(
                    <>
                        <KitButton
                            sx={{ flex: 1 }}
                            onClick={() => {
                                setFiltersOpen(false);
                                onClearFilters();
                            }}
                        >
                            Limpar
                        </KitButton>
                        <KitButton
                            variant="primary"
                            sx={{ flex: 1 }}
                            onClick={() => {
                                setFiltersOpen(false);
                                onApplyFilters(draft);
                            }}
                        >
                            Aplicar
                        </KitButton>
                    </>
                )}
            >
                <KitField label="Cliente">
                    <KitSelect aria-label="Cliente" value={draft.customer} onChange={(e) => setField('customer', e.target.value)}>
                        <option value="">Todos</option>
                        {customers.map((name) => <option key={name} value={name}>{name}</option>)}
                    </KitSelect>
                </KitField>
                <KitField label="Status">
                    <KitSelect aria-label="Status" value={draft.status} onChange={(e) => setField('status', e.target.value)}>
                        <option value="">Todos</option>
                        <option value="Finalizada">Finalizada (a faturar)</option>
                        <option value="Paga">Faturada</option>
                    </KitSelect>
                </KitField>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                    <KitField label="De">
                        <KitInput type="date" aria-label="Data inicial" value={toInput(draft.start)} onChange={(e) => setField('start', fromInput(e.target.value))} />
                    </KitField>
                    <KitField label="Até">
                        <KitInput type="date" aria-label="Data final" value={toInput(draft.end)} onChange={(e) => setField('end', fromInput(e.target.value))} />
                    </KitField>
                </Box>
            </MobileSheet>
        </Box>
    );
}
